"use client";


import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, Search, ChevronDown, ChevronUp, ChefHat, Users, Shield, Smartphone, Globe } from "lucide-react";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import StructuredData from "@/components/StructuredData";
import { generatePageTitle, generateMetaDescription, generateKeywordsString } from "@/lib/seoUtils";

interface FAQItem {
    id: string;
    question: string;
    answer: string;
    category: string;
    keywords: string[];
}

const FAQ = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('all');

    {/* Scroll to top when component mounts */ }
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const pageTitle = "שאלות נפוצות - Feedly";
    const metaDescription = generateMetaDescription(
        "שאלות נפוצות",
        "תשובות לשאלות הנפוצות על Feedly - איך יוצרים מתכונים, מנהלים מצרכים, משתפים מתכונים ועוד. מדריך מקיף למשתמשים"
    );

    const faqData: FAQItem[] = [
        // Getting Started
        {
            id: 'getting-started-1',
            question: 'איך מתחילים להשתמש ב-Feedly?',
            answer: 'התחלה ב-Feedly פשוטה מאוד! פשוט הירשמו לאתר, היכנסו לחשבון שלכם, והיכנסו לעמוד הראשי. שם תוכלו לבחור את המצרכים שיש לכם בבית וליצור מתכון מותאם אישית.',
            category: 'getting-started',
            keywords: ['התחלה', 'הרשמה', 'מתחילים', 'איך']
        },
        {
            id: 'getting-started-2',
            question: 'האם צריך להירשם כדי להשתמש באפליקציה?',
            answer: 'כן, נדרשת הרשמה כדי לשמור את המתכונים שלכם ולגשת לכל התכונות. ההרשמה חינמית ופשוטה - רק אימייל וסיסמה.',
            category: 'getting-started',
            keywords: ['הרשמה', 'חינמי', 'חשבון', 'אימייל']
        },
        {
            id: 'getting-started-3',
            question: 'איך מוסיפים מצרכים לחשבון שלי?',
            answer: 'ניתן להוסיף מצרכים בשתי דרכים: 1) דרך עמוד "המצרכים שלי" - שם תוכלו להוסיף מצרכים ידנית, 2) דרך יצירת מתכון - האפליקציה תציע לכם להוסיף מצרכים חדשים.',
            category: 'getting-started',
            keywords: ['מצרכים', 'הוספה', 'ניהול', 'מקרר']
        },

        // Recipe Creation
        {
            id: 'recipes-1',
            question: 'איך יוצרים מתכון חדש?',
            answer: 'היכנסו לעמוד הראשי, בחרו את המצרכים שיש לכם בבית מהרשימה, בחרו העדפות תזונתיות (אם יש), ולחצו על "צור מתכון". האפליקציה תיצור עבורכם מתכון מותאם אישית תוך מספר שניות.',
            category: 'recipes',
            keywords: ['מתכון', 'יצירה', 'מצרכים', 'העדפות']
        },
        {
            id: 'recipes-2',
            question: 'איך בוחרים העדפות תזונתיות?',
            answer: 'ניתן לבחור העדפות תזונתיות בעת יצירת המתכון. בעמוד יצירת המתכון, תוכלו לבחור מתוך האפשרויות: ללא גלוטן, טבעוני, צמחוני, דל קלוריות, או עשיר בחלבון.',
            category: 'recipes',
            keywords: ['העדפות', 'תזונה', 'דיאטה', 'אלרגיות']
        },
        {
            id: 'recipes-3',
            question: 'האם המתכונים מותאמים לדיאטות מיוחדות?',
            answer: 'כן! האפליקציה תומכת בדיאטות שונות: טבעונית, צמחונית, ללא גלוטן, דל קלוריות, ועשיר בחלבון. פשוט בחרו את ההעדפות שלכם בעת יצירת המתכון.',
            category: 'recipes',
            keywords: ['דיאטה', 'טבעוני', 'צמחוני', 'ללא גלוטן']
        },
        {
            id: 'recipes-4',
            question: 'איך שומרים מתכונים?',
            answer: 'לאחר יצירת מתכון, תוכלו לשמור אותו על ידי לחיצה על כפתור "שמור" בתחתית המתכון. המתכונים השמורים יופיעו בספר המתכונים שלכם.',
            category: 'recipes',
            keywords: ['שמירה', 'ספר מתכונים', 'מתכונים שמורים']
        },

        // Sharing
        {
            id: 'sharing-1',
            question: 'איך משתפים מתכונים עם אחרים?',
            answer: 'כל מתכון כולל כפתור "שתף" שמאפשר לכם ליצור קישור שיתוף. פשוט העתיקו את הקישור ושלחו לחברים ובני משפחה. הם יוכלו לראות את המתכון גם בלי להירשם לאתר.',
            category: 'sharing',
            keywords: ['שיתוף', 'קישור', 'חברים', 'משפחה']
        },
        {
            id: 'sharing-2',
            question: 'האם אפשר לשתף מתכונים ברשתות חברתיות?',
            answer: 'כן! תוכלו לשתף מתכונים בפייסבוק, ווטסאפ, טלגרם ועוד. האפליקציה יוצרת תמונה יפה של המתכון לשיתוף.',
            category: 'sharing',
            keywords: ['פייסבוק', 'ווטסאפ', 'טלגרם', 'רשתות חברתיות']
        },


        // Technical Support
        {
            id: 'technical-1',
            question: 'האפליקציה לא עובדת - מה לעשות?',
            answer: 'נסו לרענן את הדף או לסגור ולפתוח את הדפדפן. אם הבעיה נמשכת, בדקו את החיבור לאינטרנט או נסו דפדפן אחר. אם כלום לא עוזר, צרו איתנו קשר.',
            category: 'technical',
            keywords: ['לא עובד', 'בעיה', 'דפדפן', 'אינטרנט']
        },
        {
            id: 'technical-2',
            question: 'האם האפליקציה עובדת במובייל?',
            answer: 'כן! האפליקציה מותאמת למובייל ועובדת מצוין על כל המכשירים - סמארטפונים, טאבלטים ומחשבים.',
            category: 'technical',
            keywords: ['מובייל', 'סמארטפון', 'טאבלט', 'מחשב']
        },

        // Privacy & Security
        {
            id: 'privacy-1',
            question: 'האם המידע שלי בטוח?',
            answer: 'כן! אנחנו משתמשים בהצפנה מתקדמת ומגנים על המידע שלכם. אנחנו לא מוכרים או משתפים את המידע עם צדדים שלישיים.',
            category: 'privacy',
            keywords: ['בטוח', 'אבטחה', 'הצפנה', 'פרטיות']
        },
        {
            id: 'privacy-2',
            question: 'איך מוחקים את החשבון?',
            answer: 'כדי למחוק את החשבון שלכם, אנא שלחו אימייל ל-feedlyservice@gmail.com עם בקשה למחיקת החשבון. נטפל בבקשה שלכם ונמחק את כל המידע שלכם מהמערכת. שימו לב שפעולה זו לא הפיכה וכל המידע שלכם יימחק לצמיתות.',
            category: 'privacy',
            keywords: ['מחיקה', 'חשבון', 'לא הפיכה', 'מידע', 'אימייל']
        }
    ];

    const categories = [
        { id: 'all', label: 'הכל', icon: Globe },
        { id: 'getting-started', label: 'התחלה', icon: ChefHat },
        { id: 'recipes', label: 'מתכונים', icon: ChefHat },
        { id: 'sharing', label: 'שיתוף', icon: Users },
        { id: 'technical', label: 'תמיכה טכנית', icon: Smartphone },
        { id: 'privacy', label: 'פרטיות ואבטחה', icon: Shield }
    ];

    const filteredFAQs = faqData.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const toggleExpanded = (id: string) => {
        setExpandedItems(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        }))
    };

    return (
        <>
            {/* Metadata moved to metadata export or layout */}

            {/* FAQ structured data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(structuredData)
                }}
            />

            <div className="min-h-screen bg-background" dir="rtl">
                <Navigation />

                <div className="container mx-auto px-4 py-8">
                    <div className="max-w-4xl mx-auto">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <div className="bg-orange-100 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                                <HelpCircle className="h-10 w-10 text-orange-600" />
                            </div>
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">
                                שאלות נפוצות
                            </h1>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                מצאנו את התשובות לשאלות הנפוצות ביותר על Feedly
                            </p>
                        </div>

                        {/* Search */}
                        <Card className="mb-6 shadow-sm">
                            <CardContent className="p-6">
                                <div className="relative">
                                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <Input
                                        type="text"
                                        placeholder="חפשו שאלות ותשובות..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pr-10 h-11"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Categories */}
                        <div className="mb-6">
                            <div className="flex flex-wrap gap-2 justify-center">
                                {categories.map((category) => {
                                    const IconComponent = category.icon;
                                    return (
                                        <button
                                            key={category.id}
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${selectedCategory === category.id
                                                ? 'bg-orange-500 text-white border-orange-500'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-orange-50'
                                                }`}
                                        >
                                            <IconComponent className="h-4 w-4" />
                                            {category.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Results Count */}
                        <div className="mb-6 text-center">
                            <p className="text-gray-600">
                                נמצאו {filteredFAQs.length} שאלות
                                {searchTerm && ` עבור "${searchTerm}"`}
                                {selectedCategory !== 'all' && ` בקטגוריה "${categories.find(c => c.id === selectedCategory)?.label}"`}
                            </p>
                        </div>

                        {/* FAQ Items */}
                        <div className="space-y-4">
                            {filteredFAQs.map((faq) => {
                                const isExpanded = expandedItems.includes(faq.id);
                                return (
                                    <Card key={faq.id} className="shadow-sm">
                                        <CardHeader
                                            className="cursor-pointer hover:bg-orange-50 transition-colors"
                                            onClick={() => toggleExpanded(faq.id)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <CardTitle className="text-lg text-right pr-4">
                                                    {faq.question}
                                                </CardTitle>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="text-xs">
                                                        {categories.find(c => c.id === faq.category)?.label}
                                                    </Badge>
                                                    {isExpanded ? (
                                                        <ChevronUp className="h-5 w-5 text-orange-600" />
                                                    ) : (
                                                        <ChevronDown className="h-5 w-5 text-orange-600" />
                                                    )}
                                                </div>
                                            </div>
                                        </CardHeader>
                                        {isExpanded && (
                                            <CardContent className="pt-0">
                                                <p className="text-gray-700 leading-relaxed text-right">
                                                    {faq.answer}
                                                </p>
                                            </CardContent>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>

                        {/* No Results */}
                        {filteredFAQs.length === 0 && (
                            <Card className="text-center shadow-sm">
                                <CardContent className="p-8">
                                    <HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                        לא נמצאו תוצאות
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        נסו לחפש במילים אחרות או בחרו קטגוריה אחרת
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedCategory('all');
                                        }}
                                        className="text-orange-600 hover:text-orange-700 font-medium"
                                    >
                                        נקה חיפוש
                                    </button>
                                </CardContent>
                            </Card>
                        )}

                        {/* Contact CTA */}
                        <Card className="mt-8 text-center shadow-sm">
                            <CardContent className="p-8">
                                <h3 className="text-2xl font-bold mb-4 text-gray-800">
                                    לא מצאתם את התשובה?
                                </h3>
                                <p className="text-lg text-gray-600 mb-6">
                                    צוות התמיכה שלנו כאן לעזור לכם!
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <a
                                        href="mailto:feedlyservice@gmail.com"
                                        className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
                                    >
                                        <HelpCircle className="h-4 w-4 mr-2" />
                                        שלחו אימייל
                                    </a>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};

export default FAQ;
